import { useEffect, useMemo, useState } from 'react';

import { Combobox } from '@/components/ui/Combobox';
import type { ComboboxOption } from '@/components/ui/Combobox';
import { AccountLedgerButton, ledgerShortcut } from '@/components/erp/AccountLedgerButton';

import { accountApi }  from '@/api/account.api';
import { roleApi }     from '@/api/role.api';
import { userApi }     from '@/api/user.api';
import { locationApi } from '@/api/location.api';
import { bankApi, bankAccountApi } from '@/api/bank.api';
import { authApi }     from '@/api/auth.api';
import { customerApi } from '@/api/customer.api';
import { supplierApi } from '@/api/supplier.api';

/**
 * Searchable pickers for the things a form refers to by id.
 *
 * These exist because a dozen screens asked the operator to type a GUID — "GL Account ID (GUID)",
 * "Paste role UUID…", "Entity ID (GUID)". Nobody can type one, and nobody should be reading ids out
 * of one screen to key into another. Each picker below stores the same id the form always stored;
 * only the way it is chosen changes.
 *
 * One module rather than a picker per page, because the same six lists are wanted over and over and
 * six divergent copies of "load a list, map it to options" is how the labels drift apart.
 */

interface LookupProps {
  label?: string;
  /** The id, or '' for none. */
  value: string | null | undefined;
  onChange: (value: string | null) => void;
  placeholder?: string;
  hint?: string;
  error?: string;
  disabled?: boolean;
  className?: string;
}

/**
 * Loads a list once and turns it into options.
 *
 * A failed load leaves the list empty and the field says so, rather than blocking the form: the
 * server validates the id on save either way, so a lookup outage should cost the operator a
 * convenience, not the ability to work.
 */
function useLookup<T>(
  load: () => Promise<T[]>,
  toOption: (row: T) => ComboboxOption,
  deps: unknown[] = [],
) {
  const [rows, setRows] = useState<T[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let current = true;
    setLoading(true);
    load()
      .then(r => { if (current) setRows(r); })
      .catch(() => { if (current) setRows([]); })
      .finally(() => { if (current) setLoading(false); });
    return () => { current = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  const options = useMemo(() => rows.map(toOption), [rows]);   // eslint-disable-line react-hooks/exhaustive-deps
  return { options, loading, rows };
}

/** The shared shell: every picker below differs only in what it loads and how it labels a row. */
const LookupField = ({
  label, value, onChange, placeholder, hint, error, disabled, className,
  options, loading, emptyText,
}: LookupProps & { options: ComboboxOption[]; loading: boolean; emptyText: string }) => (
  <Combobox
    label={label}
    options={options}
    value={value ?? ''}
    onChange={v => onChange(v || null)}
    disabled={disabled || loading}
    error={error}
    hint={hint}
    placeholder={loading ? 'Loading…' : options.length === 0 ? emptyText : (placeholder ?? 'Select…')}
    className={className}
  />
);

// ── GL account ───────────────────────────────────────────────────────────────

/** Posting accounts only by default — the ones a transaction can actually name. */
export const GlAccountLookup = ({ postingOnly = true, ...props }: LookupProps & { postingOnly?: boolean }) => {
  const { options, loading } = useLookup(
    () => accountApi.getList({ pageNumber: 1, pageSize: 500 })
            .then(r => (r.data.data?.items ?? [])
              .filter(a => a.isActive !== false && (!postingOnly || a.isPostingAccount !== false))),
    a => ({ value: a.id, label: `${a.code} — ${a.name}` }),
    [postingOnly]);

  // Ledger link under the field (422175): the combobox has no room inside its box. className moves
  // to the wrapper so the caller's grid span still applies.
  const { className, ...rest } = props;
  return (
    <div className={className} onKeyDownCapture={ledgerShortcut(props.value)}>
      <LookupField {...rest} options={options} loading={loading}
                   emptyText="No accounts available" />
      <AccountLedgerButton accountId={props.value} withText className="mt-1" />
    </div>
  );
};

// ── Role ─────────────────────────────────────────────────────────────────────

export const RoleLookup = (props: LookupProps) => {
  const { options, loading } = useLookup(
    () => roleApi.getList().then(r => (r.data.data ?? []).filter(x => x.isActive)),
    r => ({ value: r.id, label: `${r.code} — ${r.name}` }));

  return <LookupField {...props} options={options} loading={loading}
                      emptyText="No roles set up" />;
};

// ── User ─────────────────────────────────────────────────────────────────────

export const UserLookup = (props: LookupProps) => {
  const { options, loading } = useLookup(
    () => userApi.getList({ pageNumber: 1, pageSize: 500 }).then(r => r.data.data?.items ?? []),
    // Username as well as full name: two people can share a name, and the username is what the
    // rest of the system shows them by.
    u => ({ value: u.id, label: `${u.username} — ${u.fullName || u.displayName || ''}`.trim().replace(/ —\s*$/, '') }));

  return <LookupField {...props} options={options} loading={loading}
                      emptyText="No users found" />;
};

// ── Location ─────────────────────────────────────────────────────────────────

export const LocationLookup = (props: LookupProps) => {
  const { options, loading } = useLookup(
    () => locationApi.getList({ pageNumber: 1, pageSize: 300 })
            .then(r => (r.data.data?.items ?? []).filter(l => l.isActive)),
    l => ({ value: l.id, label: `${l.code} — ${l.name}` }));

  return <LookupField {...props} options={options} loading={loading}
                      emptyText="No locations set up" />;
};

// ── Bank ─────────────────────────────────────────────────────────────────────

export const BankLookup = (props: LookupProps) => {
  const { options, loading } = useLookup(
    () => bankApi.getList({ pageNumber: 1, pageSize: 200 })
            .then(r => (r.data.data?.items ?? []).filter(b => b.isActive)),
    b => ({ value: b.id, label: `${b.bankCode} — ${b.bankName}` }));

  return <LookupField {...props} options={options} loading={loading}
                      emptyText="No banks set up" />;
};

// ── Bank account ─────────────────────────────────────────────────────────────

/** Optionally narrowed to one bank — an account belongs to exactly one. */
export const BankAccountLookup = ({ bankId, ...props }: LookupProps & { bankId?: string | null }) => {
  const { options, loading } = useLookup(
    () => bankAccountApi.getList({ pageNumber: 1, pageSize: 300 })
            .then(r => (r.data.data?.items ?? [])
              .filter(a => a.isActive && (!bankId || a.bankId === bankId))),
    // The number, not just the name: two accounts can share a name and the number is what is
    // printed on the statement.
    a => ({ value: a.id, label: `${a.accountNumber} — ${a.accountName}` }),
    [bankId]);

  return <LookupField {...props} options={options} loading={loading}
                      emptyText={bankId ? 'No accounts at this bank' : 'No bank accounts set up'} />;
};

// ── Company / Customer / Supplier ────────────────────────────────────────────

/**
 * The companies the signed-in user may work in — not the company register.
 *
 * /companies is gated on VIEW_ADMIN_COMPANY, so for everyone who is not a system administrator
 * this picker came back empty with nothing said, and a tax registration could not be created at
 * all because its Company field is mandatory. Choosing a company you already have access to is
 * not an administrative act; the context switcher's own list is the right source, and it is
 * scoped to that access rather than showing every company on the installation.
 */
export const CompanyLookup = (props: LookupProps) => {
  const { options, loading } = useLookup(
    () => authApi.accessibleCompanies().then(r => r.data.data ?? []),
    c => ({ value: c.id, label: `${c.code} — ${c.name}` }));

  return <LookupField {...props} options={options} loading={loading}
                      emptyText="No companies found" />;
};

export const CustomerLookup = (props: LookupProps) => {
  const { options, loading } = useLookup(
    () => customerApi.getList({ pageNumber: 1, pageSize: 500 }).then(r => r.data.data?.items ?? []),
    c => ({ value: c.id, label: `${c.code} — ${c.name}` }));

  return <LookupField {...props} options={options} loading={loading}
                      emptyText="No customers found" />;
};

export const SupplierLookup = (props: LookupProps) => {
  const { options, loading } = useLookup(
    () => supplierApi.getList({ pageNumber: 1, pageSize: 500 }).then(r => r.data.data?.items ?? []),
    s => ({ value: s.id, label: `${s.code} — ${s.name}` }));

  return <LookupField {...props} options={options} loading={loading}
                      emptyText="No suppliers found" />;
};

/**
 * The party a record is about, when the KIND of party is itself a field.
 *
 * Tax registrations belong to a company, a customer or a supplier, so the list has to follow the
 * Entity Type beside it — otherwise the operator picks from the wrong register and the id looks
 * valid while pointing at nothing.
 */
export const PartyLookup = ({ entityType, ...props }: LookupProps & { entityType: string }) => {
  switch (entityType) {
    case 'Customer': return <CustomerLookup {...props} />;
    case 'Supplier': return <SupplierLookup {...props} />;
    default:         return <CompanyLookup  {...props} />;
  }
};
